import { createFileRoute } from "@tanstack/react-router";
import ArticlePage from "@/pages/ArticlePage";
import { ARTICLES, type Article } from "@/data/articlesData";
import { buildHead, SITE_URL, ORG_ID } from "@/lib/seo-head";
import { toISODate } from "@/lib/seo";

function resolveArticle(slug: string | undefined) {
  if (!slug) return null;
  const article = ARTICLES.find((a) => a.slug === slug);
  return article ? { article } : null;
}

export const Route = createFileRoute("/insights/$slug")({
  head: ({ loaderData }) => {
    const data = loaderData as unknown as { article: Article } | null;
    if (!data) {
      return buildHead({
        title: "Article Not Found | Abacus Insights",
        description:
          "The article you are looking for may have been moved or is no longer available.",
        path: "/insights",
        noIndex: true,
      });
    }
    const a = data.article;
    const title = a.seoTitle || `${a.title} | Abacus Insights`;
    const description = a.seoDescription || a.excerpt;
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${SITE_URL}/insights/${a.slug}#article`,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${SITE_URL}/insights/${a.slug}`,
      },
      headline: a.title,
      description: a.excerpt,
      image: a.image,
      datePublished: toISODate(a.date),
      author: { "@type": "Organization", name: a.author },
      publisher: { "@id": ORG_ID },
      url: `${SITE_URL}/insights/${a.slug}`,
    };
    const schemas: object[] = [articleSchema];
    if (a.faqs && a.faqs.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: a.faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
    }
    return buildHead({
      title,
      description,
      path: `/insights/${a.slug}`,
      ogImage: a.image,
      ogType: "article",
      schemaJson: schemas,
    });
  },
  loader: ({ params }) => resolveArticle(params.slug),
  component: ArticlePage,
});
